import {
  DocFyber,
  DocAvatar,
  DocImage,
  DocImageHolder,
  DocMovieClip,
  DocSpine,
  DocText,
  DocStyledText,
  DocVectorGraphics,
  DocVideo,
  TextTrackSprite,
  LicensedFeature,
  ManifestVideoOptions,
  ManifestFontOptions,
  ManifestImageOptions,
  ManifestSoundOptions,
  LicensedManifestItem,
  VoiceOverAudioItem,
  DocVideoAvatar,
  DocActorAvatar,
  BasePlaybackSettings,
  AudioTrack
} from 'powtoon-versioning/src/currentSchema'

import {LibraryItem} from 'powtoon-commons/models/types'
import {
  ASSET_TYPES,
  ITEM_TYPES,
  FILE_FORMATS,
  AUDIO_TYPES,
  MEDIA_SOURCES,
  USER_ASSET_ID_PREFIXES,
  STORYBLOCKS_SOUND_EFFECT,
  AudioDataProvider
} from 'powtoon-document/consts'
import {UserMediaVideoItem} from 'powtoon-services/user-media-library/types'

export type ItemType = DocText | DocMovieClip
| DocVectorGraphics | DocSpine | DocFyber | DocImage | DocImageHolder | DocVideo | DocAvatar
// TODO make better type guards during currentSchema refactoring
export type FlippableItemType = DocVectorGraphics | DocMovieClip
  | DocSpine | DocFyber | DocImageHolder | DocVideo | DocImage

export type TintableItemType = DocVectorGraphics | DocMovieClip | DocSpine  | DocFyber | DocImageHolder

export type ColorableItemType = DocVectorGraphics | DocMovieClip | DocSpine  | DocFyber | DocImageHolder | DocStyledText

export type SpriteAllowedInFrames = DocImage | DocVideo | DocAvatar

export const TintableItemTypes = [
  ITEM_TYPES.VECTOR_GRAPHICS,
  ITEM_TYPES.MOVIE_CLIP,
  ITEM_TYPES.SPINE,
  ITEM_TYPES.FYBER,
  ITEM_TYPES.IMAGE_HOLDER
]

export const ColorableItemTypes = [
  ITEM_TYPES.VECTOR_GRAPHICS,
  ITEM_TYPES.MOVIE_CLIP,
  ITEM_TYPES.SPINE,
  ITEM_TYPES.FYBER,
  ITEM_TYPES.IMAGE_HOLDER,
  ITEM_TYPES.TEXT

]

export function isAssetItem({assetType}: Partial<LibraryItem>) {
  return assetType !== undefined
}

export function isTransitionItem({assetType}: Partial<LibraryItem>) {
  return assetType === ASSET_TYPES.TRANSITION
}

export function isCharacterItem({assetType}: Partial<LibraryItem>) {
  return assetType === ASSET_TYPES.CHARACTER
}

export function isObjectItem({assetType}: Partial<LibraryItem>) {
  return assetType === ASSET_TYPES.OBJECT
}

export function isGifItem({fileFormat}: Partial<LibraryItem>) {
  return fileFormat === FILE_FORMATS.GIF
}

export function isSpineItem(item: ItemType): item is DocSpine {
  return item.itemType === ITEM_TYPES.SPINE
}

export function isFyberItem(item: Partial<LibraryItem>): item is DocFyber {
  return item.itemType === ITEM_TYPES.FYBER
}

export function isVectorItem(item: ItemType): item is DocMovieClip | DocVectorGraphics {
  return [ITEM_TYPES.VECTOR_GRAPHICS, ITEM_TYPES.MOVIE_CLIP].includes(item.itemType)
}

export function isVectorGraphicsItem(item: ItemType): item is DocVectorGraphics {
  return item.itemType === ITEM_TYPES.VECTOR_GRAPHICS
}

export function isMovieClipItem(item: ItemType): item is DocMovieClip {
  return item.itemType === ITEM_TYPES.MOVIE_CLIP
}

export function isAnimatedItem(item: ItemType): item is DocMovieClip | DocSpine | DocFyber {
  return [ITEM_TYPES.MOVIE_CLIP, ITEM_TYPES.SPINE, ITEM_TYPES.FYBER].includes(item.itemType)
}

export function isImageHolderItem(item: Partial<LibraryItem>): item is DocImageHolder {
  return item.assetType === ASSET_TYPES.IMAGE_HOLDER
}

export function isImageHolderItemWithVideoContent(item: Partial<LibraryItem>) {
  return item.assetType === ASSET_TYPES.IMAGE_HOLDER && !!item.content && isVideoItem(item.content)
}

export function isImageHolderItemWithAvatarContent(item: Partial<LibraryItem>) {
  return item.assetType === ASSET_TYPES.IMAGE_HOLDER && !!item.content && isAvatarItem(item.content)
}

export function isUserImageItem(item: any): item is DocImage | ManifestImageOptions {
  return item.itemType === ITEM_TYPES.IMAGE && !item.assetType
}

export function isVideoBackgroundItem({itemType, assetType}: Partial<LibraryItem>) {
  return itemType === ITEM_TYPES.VIDEO && !assetType
}

export function isAvatarVideoItem (item: any): item is DocVideoAvatar {
  return isAvatarItem(item) && item.itemType === ITEM_TYPES.VIDEO
}

export function isActorItem (item: any): item is DocActorAvatar {
  return item?.itemType === ITEM_TYPES.ACTOR
}

export function isAvatarItem (item: any): item is DocAvatar {
  return item.assetType === ASSET_TYPES.AVATAR
}

export function isImageItem (item: any): item is DocImage {
  return item.itemType === ITEM_TYPES.IMAGE
}

export function isTextItem(item: Partial<LibraryItem>): item is TextTrackSprite {
  return item.itemType === ITEM_TYPES.TEXT
}

export function isColorItem({itemType}: Partial<LibraryItem>) {
  return itemType === ITEM_TYPES.COLOR
}

export function isFontItem(item: any): item is ManifestFontOptions {
  return item.itemType === ITEM_TYPES.FONT
}

export function isSoundItem(item: any): item is ManifestSoundOptions {
  return item.itemType === ITEM_TYPES.SOUND
}

export function isVideoItem(item: any): item is DocVideo | ManifestVideoOptions | UserMediaVideoItem {
  return item?.itemType === ITEM_TYPES.VIDEO && !isAvatarItem(item)
}

export function isAudibleItem(item: any) {
  return item?.itemType === ITEM_TYPES.VIDEO || item?.itemType === ITEM_TYPES.SOUND
}

export function isCameraItem(item: Partial<LibraryItem>) {
  return item.itemType === ITEM_TYPES.CAMERA
}

export function isRecordingItem({itemType, id}: Partial<LibraryItem>) {
  return isVideoItem({itemType}) && (id as string).startsWith('recording-')
}

export function isSoundAssetItem({itemType, assetType}: Partial<LibraryItem>) {
  return itemType === ITEM_TYPES.SOUND && assetType === ASSET_TYPES.MUSIC
}

export function isUserSoundItem(item: any) {
  return !item.source && item.itemType === ITEM_TYPES.SOUND && !item.assetType
}

export function isSoundtrackItem({audioType}: Partial<LibraryItem>) {
  return audioType === AUDIO_TYPES.SOUNDTRACK
}

export function isVoiceoverItem({audioType}: Partial<LibraryItem>) {
  return audioType === AUDIO_TYPES.VOICEOVER
}

export function isBackgroundItem({assetType}: Partial<LibraryItem>) {
  return assetType === ASSET_TYPES.BACKGROUND
}

export const isSlideBackgroundItem = (data: Partial<LibraryItem>) =>
  isVideoItem(data) || isImageItem(data) || isBackgroundItem(data) || isColorItem(data)

export const isEditableItem = (data: Partial<LibraryItem>) => isFyberItem(data as ItemType) && data.isEditable

const USER_ASSET_ID_PREFIX = 'user-asset:'

export const isUserAsset = ({itemType, id = '', contentItemId = ''}: Partial<LibraryItem>) =>
  isFyberItem({itemType} as ItemType) && (contentItemId || id).startsWith(USER_ASSET_ID_PREFIX)

export function isCompoundItem({assetType}: Partial<LibraryItem>) {
  return [ASSET_TYPES.TEMPLATE, ASSET_TYPES.SCENE, ASSET_TYPES.COMPOSITE_ASSET].includes(assetType!)
}

export function isSceneItem({assetType}: Partial<LibraryItem>) {
  return assetType === ASSET_TYPES.SCENE
}

export function isCompositeAssetItem({assetType}: Partial<LibraryItem>) {
  return assetType === ASSET_TYPES.COMPOSITE_ASSET
}

export function isFlippableItem(item: ItemType): item is FlippableItemType {
  return item.itemType !== ITEM_TYPES.TEXT
}

export function isUserFontItem({itemType, assetType}: Partial<LibraryItem>) {
  return itemType === ITEM_TYPES.FONT && !assetType
}

export const isExternalMediaItem = (item: Partial<LibraryItem>) =>
  item && item.mediaSource === MEDIA_SOURCES.EXTERNAL_MEDIA_PROVIDER

export const isEnterpriseMediaItem = (item: Partial<LibraryItem>) =>
  item && item.mediaSource === MEDIA_SOURCES.ENTREPRISE_GROUP_MEDIA

export const isRecording = ({itemType, id = ''}: Partial<LibraryItem>) =>
  isVideoItem({itemType}) && id.startsWith(USER_ASSET_ID_PREFIXES.RECORDING)

export const isNineSliceItem = (sprite: Partial<LibraryItem>) => sprite?.scaling?.method === 'nineSlice'

export const isItemPremiumSoundEffect = ({source}: Partial<LibraryItem>) => source === STORYBLOCKS_SOUND_EFFECT

export function isTintableItem(item: ItemType): item is TintableItemType {
  return TintableItemTypes.includes(item.itemType)
}

export function isColorableItem(item: ItemType): item is ColorableItemType {
  return ColorableItemTypes.includes(item.itemType)
}

export function isLicensedFeature(item: any): item is LicensedFeature {
  return !!item?.featureId
}

export function isLipSyncAudioSprite(item: Partial<LibraryItem>): item is DocFyber | DocAvatar {
  return isFyberItem(item) || isAvatarItem(item)
}

export function getLipSyncAudioTrackFromSprite(sprite: ItemType): null | VoiceOverAudioItem {
  if (isLipSyncAudioSprite(sprite)) {
    const {audioTracks: [audioTrack] = []} = sprite
    return audioTrack
  }
  return null
}

export function getLipSyncPlaybackSettingsFromSprite(sprite: ItemType): undefined | BasePlaybackSettings {
  if (isAvatarItem(sprite)) {
    return sprite.playbackSettings
  }
  else if (isFyberItem(sprite)) {
    const {audioTracks: [audioTrack] = []} = sprite
    return audioTrack?.playbackSettings
  }
  return undefined
}

export function isTTSItem(item: any) {
  return item.audioData?.source?.provider === AudioDataProvider.TextToSpeech
}

export function hasLicenseLevel(item: any): item is LicensedManifestItem {
  return !!item.licenseLevel
}

export const isMediaProviderItem = (sound?: {source?: string}) => !!sound?.source

export const isItemAllowedInFrame = (item: any): item is SpriteAllowedInFrames => (
  isImageItem(item) || isVideoItem(item) || isAvatarItem(item)
)

export function isAudioTrack(item: Partial<LibraryItem>): item is AudioTrack {
  return item.itemType === ITEM_TYPES.SOUND && (
    item.audioType === AUDIO_TYPES.SOUNDTRACK ||
    item.audioType === AUDIO_TYPES.VOICEOVER
  )
}